import {
  useDeleteAllVersionComparisonsMutation,
  useGetVersionComparisonsQuery,
  useProjectId,
  usePackageId,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { Loader2Icon, Trash2Icon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { ConfirmDialog, errorToast, successToast } from '@/components';

/**
 * Soft-deletes all version comparisons in a package via a single backend call,
 * mirroring the findings "Delete All" flow.
 */
export const DeleteAllVersionComparisonsButton = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [open, setOpen] = useState(false);

  const [deleteAll, { isLoading: isDeleting }] = useDeleteAllVersionComparisonsMutation();

  const { data, isLoading: isLoadingCount } = useGetVersionComparisonsQuery({
    projectId,
    packageId,
    page: 'all',
  });

  const count = data?.results?.length ?? 0;
  const isLoading = isLoadingCount || isDeleting;

  const handleDeleteAll = useCallback(async () => {
    try {
      const result = await deleteAll({ projectId, packageId }).unwrap();
      successToast({ message: `Successfully deleted ${result.deletedCount} version comparisons` });
    } catch (error) {
      console.error('Failed to delete all version comparisons:', error);
      errorToast({ message: 'Failed to delete version comparisons. Please try again.' });
    } finally {
      setOpen(false);
    }
  }, [projectId, packageId, deleteAll]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="destructive"
          disabled={isLoading || count === 0}
          className="gap-2 max-w-xs"
          title={`Delete All Findings (${count})`}
        >
          {isLoading ? (
            <Loader2Icon className="h-4 w-4 animate-spin shrink-0" />
          ) : (
            <Trash2Icon className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate min-w-0">Delete All Findings</span>
          <span className="shrink-0">({count})</span>
        </Button>
      }
      title="Delete all version comparisons?"
      description="This will delete all findings for this version compare package. This action cannot be easily undone."
      confirmLabel="Delete All"
      isLoading={isDeleting}
      onConfirm={handleDeleteAll}
    />
  );
};
